import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Typography, Tag, Modal } from "antd";
import {
  PlayCircleOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  MobileOutlined,
  LaptopOutlined,
  CloudUploadOutlined,
  ApiOutlined,
  BarChartOutlined,
  ToolOutlined,
  UserAddOutlined,
  LoginOutlined,
  MenuOutlined,
  UserOutlined,
  CustomerServiceOutlined,
  BugOutlined,
  AppstoreOutlined,
  FilterOutlined,
  SlidersOutlined,
  SoundOutlined,
  YoutubeOutlined,
  ArrowUpOutlined,
  PauseOutlined,
  FastForwardOutlined,
  SaveOutlined,
  PlusOutlined,
  EditOutlined,
  CopyOutlined,
  RobotOutlined,
  BulbOutlined,
  ExclamationCircleOutlined,
  BookOutlined,
  CoffeeOutlined,
  HeartFilled,
} from "@ant-design/icons";
import { useInstall } from "./InstallProvider";
import logo from "../logo_no_bg.png";

const { Title, Paragraph, Text } = Typography;

export default function LandingPage() {
  const { isStandalone, promptInstall } = useInstall();
  const topRef = useRef(null);
  const [activeStepId, setActiveStepId] = useState("signup");
  const [shotStatusByStepId, setShotStatusByStepId] = useState({});
  const [imageModal, setImageModal] = useState({ open: false, src: null, title: "" });
  const [isMobile, setIsMobile] = useState(false);

  const APP_TITLE = "SimplyRide.app";
  const APP_SLOGAN = "Plan, Train, Share";
  const HERO_MAIN_IMAGE_SRC = "/landing/0-main.png";
  const PHILOSOPHY_SOURCE_URL = null; // TODO: paste your link here when ready
  const CONTRIBUTE_URL = "https://buymeacoffee.com/sverbo";
  const DEV_BLOG_URL =
    "https://medium.com/@letsgeekabit/building-the-app-i-couldnt-find-a-simple-free-lightweight-solution-for-indoor-cycling-workouts-32544c12451a";

  // Mark landing as shown (prevents future auto-redirect from /)
  useEffect(() => {
    try {
      localStorage.setItem("isLandingShown", "true");
    } catch {
      // ignore storage errors
    }
  }, []);

  // Mobile-only tweaks (keep desktop identical)
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(Boolean(mq.matches));
    update();
    try {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    } catch {
      // Safari < 14
      mq.addListener(update);
      return () => mq.removeListener(update);
    }
  }, []);

  const openApp = () => {
    // Always open the "real app" in a new tab (not /landing)
    window.open("/", "_blank", "noopener,noreferrer");
  };

  const scrollToTop = () => {
    try {
      if (topRef.current && typeof topRef.current.scrollIntoView === "function") {
        topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      window.scrollTo(0, 0);
    }
  };

  const openImageModal = (src, title) => {
    if (!src) return;
    setImageModal({ open: true, src, title: title || "" });
  };

  const handleInstallClick = async () => {
    if (isStandalone) return;
    const result = await promptInstall();
    if (!result || result.status === "unavailable" || result.status === "error") {
      window.alert(
        "Install not available or the app is already installed.\n\nSafari (macOS): Safari → Add to Dock.\nChrome: Address bar ‘Install’ icon or 3‑dots → Save and install.\nEdge: 3‑dots → Apps → Install this site as an app."
      );
    }
  };

  const steps = useMemo(
    () => [
      {
        id: "signup",
        nav: "Sign up",
        title: "Create your free account",
        icon: <UserAddOutlined />,
        body: (
          <>
            <Paragraph>
              SimplyRide.app is <Text strong>completely free</Text> and runs in your browser, no installation required. Optional PWA installation makes the experience smoother.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Create an account, confirm your email, then log in.
            </Paragraph>
          </>
        ),
        filename: "01-signup-login.png",
        hint: "Screenshot: sign up / login screen",
      },
      {
        id: "library",
        nav: "Library",
        title: "Browse the workout library",
        icon: <AppstoreOutlined />,
        body: (
          <>
            <Paragraph>
              Pick an individual session from the library (hundreds available) or start a week-by-week program. Filter by objective, duration, category, and more. When you follow a program, SimplyRide remembers your next session and will take you there when you log back in (smooth progression).
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              If you train with friends, it’s easy to find and reuse the same sessions.
            </Paragraph>
          </>
        ),
        filename: "02-training-library-filters.png",
        hint: "Screenshot: training library with filters (duration/category/TSS) applied",
      },
      {
        id: "plan",
        nav: "Plan details",
        title: "Open a plan and understand what’s coming",
        icon: <FilterOutlined />,
        body: (
          <>
            <Paragraph>
              Plan details show the session profile and how long you’ll spend in each zone.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              You’ll also see notes (like cadence tips). Before riding, make sure you <Text strong>adjust to your FTP</Text>{" "}
              so the targets match your fitness. You can favourite a plan to find it quickly later.
            </Paragraph>
          </>
        ),
        filename: "03-plan-details-favourite.png",
        hint: "Screenshot: plan details + zone distribution + favourite button",
      },
      {
        id: "connect",
        nav: "Connect",
        title: "Connect your trainer and sensors",
        icon: <ApiOutlined />,
        body: (
          <>
            <Paragraph>
              In the control room, connect your home trainer and (if available) heart-rate sensor.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              When supported, SimplyRide.app can automatically set resistance to match power targets.
            </Paragraph>
          </>
        ),
        filename: "04-connect-controls.png",
        hint: "Screenshot: connect trainer + HR/cadence + controls area",
      },
      {
        id: "ride",
        nav: "Ride",
        title: "Ride the session step-by-step",
        icon: <ThunderboltOutlined />,
        body: (
          <>
            <Paragraph>
              Follow the targets. If ERG/control is enabled, the trainer adjusts resistance so you stay in the right
              zone.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Too hard / too easy? You can increase or decrease the <Text strong>target FTP</Text> during the session to
              scale the workout intensity.
            </Paragraph>
            <ul className="landing2-mini-bullets">
              <li>
                <SoundOutlined /> Mute/unmute interval beeps
              </li>
              <li>
                <PauseOutlined /> Pause
              </li>
              <li>
                <FastForwardOutlined /> Skip intervals (can’t go back)
              </li>
            </ul>
          </>
        ),
        filename: "05-control-room-ride.png",
        hint: "Screenshot: control room during a workout (targets + timers + chart)",
      },
      {
        id: "fun",
        nav: "Video & live",
        title: "Add motivation: YouTube or ride live with friends",
        icon: <YoutubeOutlined />,
        body: (
          <>
            <Paragraph>
              Embed any YouTube video during your session, or start a live session to train together.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              This turns the control room into a complete training cockpit.
            </Paragraph>
          </>
        ),
        filename: "06-youtube-live.png",
        extraFilenames: ["06-youtube-live-2.png"],
        hint: "Screenshot: embedded YouTube video OR live session UI",
      },
      {
        id: "save",
        nav: "Save & export",
        title: "Save your activity and export it (e.g. Strava)",
        icon: <SaveOutlined />,
        body: (
          <>
            <Paragraph>
              At the end, save the workout, optionally push to Strava, and/or download a file for platforms like Garmin
              or Polar.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              You can enter weight optionally (for estimating distance) and export later if you forgot.
            </Paragraph>
          </>
        ),
        filename: "07-save-export.png",
        hint: "Screenshot: save modal + export options (Strava / download file)",
      },
      {
        id: "history",
        nav: "History",
        title: "Track progress over time",
        icon: <BarChartOutlined />,
        body: (
          <>
            <Paragraph>
              Your history lists all sessions and provides a simple data analysis view so you can see volume, trends,
              and consistency.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              This is where training becomes measurable.
            </Paragraph>
          </>
        ),
        filename: "08-history-analysis.png",
        hint: "Screenshot: history list + analysis table/charts",
      },
      {
        id: "build",
        nav: "Create plans",
        title: "Create (or copy) your own training plans",
        icon: <ToolOutlined />,
        body: (
          <>
            <Paragraph>
              Want to go custom? Create a new plan from scratch or copy an existing one and tweak it.
            </Paragraph>
            <ul className="landing2-mini-bullets">
              <li>
                <PlusOutlined /> Add warmup / intervals / cooldown
              </li>
              <li>
                <CopyOutlined /> Duplicate selections quickly
              </li>
              <li>
                <EditOutlined /> Edit steps (and drag & drop if needed)
              </li>
            </ul>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Keep reference FTP at 100W if you like working in percentages, then scale using your real FTP.
            </Paragraph>
          </>
        ),
        filename: "09-create-edit-plan.png",
        hint: "Screenshot: create/edit plan builder (steps list + chart)",
      },
      {
        id: "icoach",
        nav: "Simpl'AI Coach",
        title: "Use Simpl'AI Coach to build a workout or full program",
        icon: <RobotOutlined />,
        body: (
          <>
            <Paragraph>
              Not sure what to do today? The <Text strong>Simpl'AI Coach</Text> can generate a single workout or a full training
              program tailored to your goals.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              You can still edit everything afterwards, it’s a fast way to start with a solid structure.
            </Paragraph>
          </>
        ),
        filename: "10-icoach.png",
        extraFilenames: ["10-icoach-bis.png"],
        hint: "Screenshot: Simpl'AI Coach / AI plan builder screen (intro or steps)",
      },
      {
        id: "tips",
        nav: "Tips & tricks",
        title: "Tips & tricks via sidebar",
        icon: <BulbOutlined />,
        body: (
          <>
            <Paragraph>
              A few things that make SimplyRide.app much smoother day-to-day:
            </Paragraph>
            <ul className="landing2-mini-bullets">
              <li>
                <MenuOutlined /> Use the sidebar to jump between pages
              </li>
              <li>
                <UserOutlined /> Update your profile (FTP/weight) and personalize your background image
              </li>
              <li>
                <CustomerServiceOutlined /> Contact support / send feedback anytime
              </li>
              <li>
                <BugOutlined /> Report bugs (trainer model + quick description helps a lot)
              </li>
              <li>
                <RocketOutlined /> Install the app for a more "native" experience
              </li>
              <li>
                <SlidersOutlined /> Set your FTP once so all power zones scale correctly
              </li>
              <li>
                <SoundOutlined /> Use beeps (or mute) to stay on interval timing
              </li>
            </ul>
          </>
        ),
        filename: "11-tips-tricks.png",
        hint: "Screenshot: sidebar open OR FTP control OR install prompt (any ‘tips’ montage is fine)",
      },
      {
        id: "mobile",
        nav: "Mobile version",
        title: "Mobile Version (simplified & installable)",
        icon: <MobileOutlined />,
        body: (
          <>
            <Paragraph>
              The <Text strong>mobile version</Text> is a simplified UI designed to be quick and readable while you ride.
              You can also install SimplyRide.app as a <Text strong>PWA</Text> for a more stable, app-like experience.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginTop: 0 }}>
              Unfortunately, it does <Text strong>not work on iOS (iPhone/iPad)</Text> because Apple does not allow the
              required Bluetooth (BLE) access in browsers. It <Text strong>does work on Mac</Text> (and other
              desktop/laptop devices).
            </Paragraph>
            <Paragraph style={{ marginBottom: 8 }}>
              For the best experience, use a <Text strong>desktop</Text> to:
            </Paragraph>
            <ul className="landing2-mini-bullets">
              <li>
                <BarChartOutlined /> Access full data analysis
              </li>
              <li>
                <FilterOutlined /> Search plans more easily
              </li>
              <li>
                <ToolOutlined /> Create plans (including with <Text strong>Simpl&apos;AI Coach</Text>)
              </li>
            </ul>
            <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              Tip: find and create your session on desktop, then tag it as <Text strong>favourite</Text> so you can
              quickly find it on mobile. Sessions done on mobile can still be uploaded to <Text strong>Strava</Text> and
              will be saved in history as usual (best: connect your Strava account first on desktop in your{" "}
              <Text strong>Profile</Text> section).
            </Paragraph>
          </>
        ),
        filename: "mobile_version.jpeg",
        hint: "Screenshot: mobile version simplified workout view",
      },
      {
        id: "limits",
        nav: "Limitations",
        title: "Limitations & compatibility",
        icon: <ExclamationCircleOutlined />,
        body: (
          <>
            <Paragraph>
              SimplyRide.app runs in a browser, so a few limitations apply:
            </Paragraph>
            <ul className="landing2-mini-bullets">
              <li>
                <MobileOutlined /> <Text strong>iPhone/iPad:</Text> Bluetooth in web apps is not supported for trainer
                control (use a laptop/desktop instead, including MacBooks).
              </li>
              <li>
                <ApiOutlined /> Needs <Text strong>Bluetooth (BLE)</Text> and an <Text strong>FTMS‑compatible</Text>{" "}
                trainer (standard Fitness Machine Service). <Text strong>ANT+</Text> is not supported in the browser.
              </li>
              <li>
                <ToolOutlined /> Some trainers use <Text strong>proprietary</Text> protocols instead of FTMS. Supporting
                those typically requires a real partnership/SDK (this is why some native apps can support more devices,
                e.g. Zwift).
              </li>
              <li>
                <LaptopOutlined /> Works with most smart trainers that support standard BLE profiles, but device support
                can vary by browser.
              </li>
            </ul>
          </>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;

    const els = steps
      .map((s) => document.getElementById(`landing-step-${s.id}`))
      .filter(Boolean);
    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const id = e.target.getAttribute("data-stepid");
            if (id) setActiveStepId(id);
          }
        }
      },
      {
        root: null,
        // mark active when the section is around the top-middle of the viewport
        rootMargin: "-35% 0px -55% 0px",
        threshold: 0.01,
      }
    );

    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [steps]);

  const scrollToStep = (id) => {
    const el = document.getElementById(`landing-step-${id}`);
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {
      // ignore
    }
  };

  return (
    <div className="landing2-root" ref={topRef}>
      <div className="landing2-container">
        <div className="landing2-hero liquid-glass">
          <div className="landing2-hero-top">
            <div className="landing2-brand">
              <img className="landing2-logo" src={logo} alt="SimplyRide.app" />
              <div>
                <div className="landing2-badges">
                  <Tag color="blue" style={{ marginInlineEnd: 6 }}>
                    v2.0
                  </Tag>
                  <Tag color="green" style={{ marginInlineEnd: 6 }}>
                    Free
                  </Tag>
                  <Tag color="geekblue">No install required</Tag>
                </div>
                <Title level={1} className="landing2-title">
                  {APP_TITLE}
                </Title>
                <Text type="secondary" className="landing2-subtitle">
                  <span className="landing2-slogan">{APP_SLOGAN}</span>
                  <br />
                  Open SimplyRide.app in any browser. It’s <b>free</b>.
                  <br />
                  Choose from <b>500+</b> workout sessions, follow a week-by-week training program, or build your own
                  with <b>Simpl&apos;AI Coach</b>. Connect your trainer, <b>simply ride</b> the session, then save and
                  export your activity.
                </Text>
                <div className="landing2-hero-lower">
                  <div className="landing2-hero-preview">
                    <img
                      src={HERO_MAIN_IMAGE_SRC}
                      alt="SimplyRide.app preview"
                      className="landing2-hero-preview-img"
                      loading="lazy"
                      decoding="async"
                      onClick={() => openImageModal(HERO_MAIN_IMAGE_SRC, "App preview")}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openImageModal(HERO_MAIN_IMAGE_SRC, "App preview");
                        }
                      }}
                    />
                  </div>

                  <div className="landing2-hero-meta">
                    <Text type="secondary" className="landing2-contact">
                      Feedback or bug report? Use the in-app feedback/bug report form, or email{" "}
                      <a href="mailto:sam@simplyride.app">sam@simplyride.app</a>.
                    </Text>
                    <Text type="secondary" className="landing2-madeby">
                      Made with <HeartFilled className="landing2-heart" /> by Sam
                    </Text>
                  </div>
                </div>
              </div>
            </div>

            <div className="landing2-hero-actions">
              <Button
                type="primary"
                size="large"
                className="primary-glass-btn landing2-hero-btn"
                onClick={openApp}
              >
                Open app
              </Button>
              <Button
                size="large"
                className="landing2-hero-btn"
                onClick={() =>
                  window.open("https://www.youtube.com/watch?v=DlS4MOAhLs0", "_blank", "noreferrer")
                }
                icon={<PlayCircleOutlined />}
              >
                {isMobile ? (
                  "Demo"
                ) : (
                  <>
                    <span className="landing2-demo-prefix">Watch </span>
                    <span className="landing2-demo-word">demo</span>
                  </>
                )}
              </Button>
              <Button
                size="large"
                className="landing2-hero-btn"
                onClick={handleInstallClick}
                disabled={isStandalone}
                icon={<RocketOutlined />}
              >
                {isStandalone ? "Installed" : isMobile ? "Install" : "Install app"}
              </Button>
            </div>
          </div>

          <div className="landing2-hero-highlights">
            <div className="landing2-highlight">
              <LaptopOutlined />
              <span>Mac & Windows | Mobile-friendly</span>
            </div>
            
            <div className="landing2-highlight">
              <BookOutlined />
              <span>Browse hundreds of plans or create your own workouts</span>
            </div>
            <div className="landing2-highlight">
              <ApiOutlined />
              <span>ERG Trainer control</span>
            </div>
            <div className="landing2-highlight">
              <CloudUploadOutlined />
              <span>Export to platforms like Strava</span>
            </div>
          </div>
        </div>

        <div className="landing2-section glass-panel landing2-section-pad">
          <Title level={3} style={{ marginTop: 0 }}>
            Why this app exists
          </Title>
          <Paragraph style={{ marginBottom: 10 }}>
            This started as a <Text strong>personal project</Text>. I was frustrated with indoor cycling apps that were
            paywalled, bloated, or distracting, I just wanted structured training, without the fluff.
          </Paragraph>
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            Read more about the development project:{" "}
            <a href={DEV_BLOG_URL} target="_blank" rel="noreferrer">
              Medium article
            </a>{" "}
            (v1 - much changed since then)
          </Paragraph>

          <div className="landing2-callout landing2-callout-philosophy">
            <Text strong>This is why I built it</Text>
            <div style={{ marginTop: 6 }}>
              <Text>
                I was fed up with ads and €15–€20/month (<b>€240/year !</b>) subscriptions just to get "virtual reality avatars", fake
                gradients, and other distractions. When I train, I’m usually watching a series or listening to music,
                I just need my intervals to be clear, reliable, and easy to follow.
              </Text>
            </div>
          </div>

          <Paragraph style={{ marginBottom: 8 }}>
            SimplyRide.app focuses on three things:
          </Paragraph>
          <ul className="landing2-mini-bullets">
            <li>Create and use structured plans easily (library, coach/shared, or your own)</li>
            <li>Connect sensors and control resistance (ERG) so intervals "just work"</li>
            <li>Stay lightweight, including a mobile-friendly mode</li>
          </ul>

          <div className="landing2-inline-actions">
           
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                I’m happy to share this for free, if it helps you, feel free to buy me a coffe.
              </Text>
             
            </div>
            <Button
              onClick={() => window.open(CONTRIBUTE_URL, "_blank", "noopener,noreferrer")}
              className="landing2-hero-btn landing2-contribute-btn margin-top-8"
              icon={<CoffeeOutlined />}
            >
              Contribute
            </Button>
            {PHILOSOPHY_SOURCE_URL ? (
              <div style={{ marginTop: 6 }}>
                <Text type="secondary">
                  Full story:{" "}
                  <a href={PHILOSOPHY_SOURCE_URL} target="_blank" rel="noreferrer">
                    {PHILOSOPHY_SOURCE_URL}
                  </a>
                </Text>
              </div>
            ) : null}
          </div>
        </div>

        <div className="landing2-tour">
          <div className="landing2-rail">
            <div className="landing2-rail-title">How it works</div>
            <div className="landing2-rail-list">
              {steps.map((s, idx) => {
                const isActive = activeStepId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`landing2-rail-item ${isActive ? "is-active" : ""}`}
                    onClick={() => scrollToStep(s.id)}
                  >
                    <span className="landing2-rail-dot">{idx + 1}</span>
                    <span className="landing2-rail-text">{s.nav}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="landing2-tour-content">
            {steps.map((s, idx) => {
              const hasShot = Boolean(s.filename) && s.id !== "limits";
              const shotSizeClass =
                s.id === "tips"
                  ? "landing2-shot-img--tiny"
                  : s.id === "connect"
                    ? "landing2-shot-img--xsmall"
                    : s.id === "mobile"
                      ? "landing2-shot-img--tiny"
                    : "";
              const filenames = hasShot
                ? [s.filename, ...(Array.isArray(s.extraFilenames) ? s.extraFilenames : [])].filter(Boolean)
                : [];
              const srcs = filenames.map((fn) => ({ fn, src: `/landing/${fn}` }));
              const anyOk = srcs.some(({ fn }) => shotStatusByStepId[`${s.id}:${fn}`] === "ok");
              const allMissing =
                srcs.length > 0 && srcs.every(({ fn }) => shotStatusByStepId[`${s.id}:${fn}`] === "missing");
              return (
                <div
                  key={s.id}
                  id={`landing-step-${s.id}`}
                  data-stepid={s.id}
                  className="landing2-stepcard glass-panel"
                >
                  {isMobile ? (
                    <div className="landing2-stepcard-top landing2-stepcard-top--mobile">
                      <div className="landing2-stephead">
                        <div className="landing2-stepnum">{idx + 1}</div>
                        <span className="landing2-stepicon">{s.icon}</span>
                      </div>
                      <div className="landing2-stepmeta">
                        <Title level={3} style={{ margin: 0 }}>
                          {s.title}
                        </Title>
                        <div className="landing2-stepmeta-body">{s.body}</div>
                        {s.id === "signup" && (
                          <div className="landing2-inline-actions">
                            <Button
                              size="middle"
                              icon={<LoginOutlined />}
                              onClick={openApp}
                              className="primary-glass-btn"
                              type="primary"
                            >
                              Open app
                            </Button>
                          </div>
                        )}
                        {s.id === "tips" && (
                          <div className="landing2-inline-actions">
                            <Button
                              size="middle"
                              onClick={handleInstallClick}
                              disabled={isStandalone}
                              icon={<RocketOutlined />}
                            >
                            {isStandalone ? "Installed" : isMobile ? "Install" : "Install app"}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="landing2-stepcard-top">
                      <div className="landing2-stepnum">{idx + 1}</div>
                      <div className="landing2-stepmeta">
                        <div className="landing2-stepmeta-title">
                          <span className="landing2-stepmeta-icon">{s.icon}</span>
                          <Title level={3} style={{ margin: 0 }}>
                            {s.title}
                          </Title>
                        </div>
                        <div className="landing2-stepmeta-body">{s.body}</div>
                        {s.id === "signup" && (
                          <div className="landing2-inline-actions">
                            <Button
                              size="middle"
                              icon={<LoginOutlined />}
                              onClick={openApp}
                              className="primary-glass-btn"
                              type="primary"
                            >
                              Open app
                            </Button>
                          </div>
                        )}
                        {s.id === "tips" && (
                          <div className="landing2-inline-actions">
                            <Button
                              size="middle"
                              onClick={handleInstallClick}
                              disabled={isStandalone}
                              icon={<RocketOutlined />}
                            >
                            {isStandalone ? "Installed" : isMobile ? "Install" : "Install app"}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {hasShot ? (
                    <div className="landing2-stepcard-shot">
                      <div className="landing2-shot-frame landing2-shot-frame-wide">
                        {srcs.map(({ fn, src }, i) => {
                          const st = shotStatusByStepId[`${s.id}:${fn}`] || "unknown";
                          if (st === "missing") return null;
                          return (
                            <div
                              key={fn}
                              className={[
                                "landing2-shot-wrap",
                                i > 0 ? "landing2-shot-wrap--stacked" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            >
                              <img
                                className={[
                                  "landing2-shot-img",
                                  "landing2-shot-img-wide",
                                  shotSizeClass,
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                                src={src}
                                alt={s.title}
                                role="button"
                                tabIndex={0}
                                onClick={() => openImageModal(src, s.title)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openImageModal(src, s.title);
                                  }
                                }}
                                onLoad={() => {
                                  setShotStatusByStepId((prev) => ({ ...(prev || {}), [`${s.id}:${fn}`]: "ok" }));
                                }}
                                onError={() => {
                                  setShotStatusByStepId((prev) => ({ ...(prev || {}), [`${s.id}:${fn}`]: "missing" }));
                                }}
                              />
                            </div>
                          );
                        })}

                        {/* Only show filename + instructions when screenshot is missing */}
                        {!anyOk && allMissing ? (
                          <>
                            <div className="landing2-shot-label">{s.nav}</div>
                            <div className="landing2-shot-hint">
                              Add screenshot{filenames.length > 1 ? "s" : ""}:{" "}
                              {filenames.map((fn, i) => (
                                <React.Fragment key={fn}>
                                  <Text code>{fn}</Text>
                                  {i < filenames.length - 1 ? ", " : null}
                                </React.Fragment>
                              ))}{" "}
                              in <Text code>frontend/public/landing/</Text>
                              <br />
                              {s.hint}
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                </div>
              );
            })}

            {/* Final action card (separate from step 12) */}
            <div className="landing2-stepcard glass-panel landing2-stepcard--end">
              <div className="landing2-stepcard-bottom">
                <Button
                  className="primary-glass-btn landing2-goTopBtn"
                  type="primary"
                  icon={<ArrowUpOutlined />}
                  onClick={scrollToTop}
                >
                  Back to top
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="landing2-footer">
          <Text type="secondary">
            Tip: installing the app can improve stability and reduce gaps in data points.
          </Text>
          <div className="landing2-footer-madeby">
            <Text type="secondary">
              Made with <HeartFilled className="landing2-heart" /> by Sam
            </Text>
          </div>
        </div>
      </div>

      <Modal
        open={imageModal.open}
        title={imageModal.title || "Screenshot"}
        footer={null}
        onCancel={() => setImageModal({ open: false, src: null, title: "" })}
        width="min(1540px, 98vw)"
        centered
        rootClassName="liquid-glass-modal"
      >
        {imageModal.src ? (
          <img
            src={imageModal.src}
            alt={imageModal.title || "Screenshot"}
            className="landing2-modal-img"
          />
        ) : null}
      </Modal>
    </div>
  );
}


